import {DataTable} from "../DataTable/DataTable";
import {DispatchEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {Anchor, Box, Group, Modal, ModalProps, Stack} from "@mantine/core";
import {Link} from "react-router-dom";
import {usePagination} from "../DataTable/usePagination";
import {useDispatchDataTableFilter} from "../DispatchDataTable/useDispatchDataTableFilter";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {useMemo} from "react";
import {DataTableColumn} from "mantine-datatable";
import {DispatchProgress} from "../../progress/DispatchProgress/DispatchProgress";
import {FormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import {EntityAnchor} from "../../anchor/EntityAnchor/EntityAnchor";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {FormattedQuantity} from "../../text/FormattedQuantity/FormattedQuantity";

const EMPTY_ARRAY: DispatchEntity[] = []

export type DispatchDataTableProps = EntityDataTableProps<DispatchEntity> & {}

export const DispatchDataTable = (props: DispatchDataTableProps) => {
  const {
    records: dispatches = EMPTY_ARRAY,
    recordsPerPageOptions,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);

  const {filteredRecords, filterConfig} = useDispatchDataTableFilter({
    records: dispatches
  })

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'scheduleDttm', direction: 'asc'},
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const columns = useMemo(() => {
    const values: DataTableColumn<DispatchEntity>[] = [
      {
        title: t("common.entity.dispatch.table.column.date", "Scheduled"),
        accessor: 'scheduleDttm',
        width: 100,
        sortable: true,
        render: ({id, scheduleDttm}) => (
          <Anchor component={Link} to={`/dispatch/profile?id=${id}`}>
            {scheduleDttm ? <FormattedDateTime date={scheduleDttm}/> : t("common.actions.view", "View")}
          </Anchor>
        ),
      },
      {
        title: t("table.entity.dispatch.column.quantity", "Quantity"),
        accessor: "quantity",
        sortable: true,
        width: 125,
        render: ({quantity, quantityUom}) => (
          <FormattedQuantity value={{number: quantity, units: quantityUom}} />
        )
      },
      {
        title: t("table.entity.dispatch.column.freighttype", "Freight Type"),
        accessor: "freightTypeName",
        width: 125,
        sortable: true,
        render: ({freightTypeId, freightTypeName}) => (
          freightTypeId ? <DefaultText value={freightTypeName}/> : ""
        )
      },
      {
        title: t("table.entity.dispatch.column.servicetype", "Service Type"),
        accessor: "serviceTypeName",
        width: 125,
        sortable: true,
        render: ({pickupServiceTypeId, pickupServiceTypeName}) => (
          pickupServiceTypeId ? <DefaultText value={pickupServiceTypeName}/> : ""
        )
      },
      {
        title: t("common.entity.dispatch.table.column.pickup", "Pickup"),
        accessor: 'pickup',
        sortable: false,
        width: 400,
        render: (dispatch) => (
          <Stack spacing="xs">
            <Group>
              {"(S): "}
              {dispatch.pickupSiteId ? <EntityAnchor value={{ objectId: dispatch.pickupSiteId, objectName: dispatch.pickupSiteName, objectType: "SITE"}} /> : t("common.shared.unspecified", "Unspecified")}
            </Group>
            <Group>
              {"(W): "}
              {dispatch.pickupWellId ? <EntityAnchor value={{ objectId: dispatch.pickupWellId, objectName: dispatch.pickupWellName, objectType: "WELL"}} /> : t("common.shared.unspecified", "Unspecified")}
            </Group>
            <Group>
              {"(C): "}
              {dispatch.pickupContainerId ? <EntityAnchor value={{ objectId: dispatch.pickupContainerId, objectName: dispatch.pickupContainerName, objectType: "CONTAINER"}} /> : t("common.shared.unspecified", "Unspecified")}
            </Group>
          </Stack>
        ),
      },
      {
        title: t("common.entity.dispatch.table.column.delivery", "Delivery"),
        accessor: 'delivery',
        sortable: false,
        width: 400,
        render: (dispatch) => (
          <Stack spacing="xs">
            <Group>
              {"(S): "}
              {dispatch.deliverySiteId ? <EntityAnchor value={{ objectId: dispatch.deliverySiteId, objectName: dispatch.deliverySiteName, objectType: "SITE"}} /> : t("common.shared.unspecified", "Unspecified")}
            </Group>
            <Group>
              {"(W): "}
              {dispatch.deliveryWellId ? <EntityAnchor value={{ objectId: dispatch.deliveryWellId, objectName: dispatch.deliveryWellName, objectType: "WELL"}} /> : t("common.shared.unspecified", "Unspecified")}
            </Group>
            <Group>
              {"(C): "}
              {dispatch.deliveryContainerId ? <EntityAnchor value={{ objectId: dispatch.deliveryContainerId, objectName: dispatch.deliveryContainerName, objectType: "CONTAINER"}} /> : t("common.shared.unspecified", "Unspecified")}
            </Group>
          </Stack>
        ),
      },
      {
        title: t("table.entity.dispatch.column.status", "Status"),
        accessor: "status",
        width: 150,
        render: (dispatch) => (
          <Stack>
            <DispatchProgress
              size={12}
              status={dispatch.status}
              pickup={typeof dispatch.pickupTransportId !== 'undefined'}/>
          </Stack>
        ),
      },
    ]

    return values
  }, [filterConfig, t])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={columns}
    />
  )
}

export type DispatchDataTableModalProps = Omit<ModalProps, "onSubmit"> & {
  dataTable: Omit<DispatchDataTableProps, 'recordsPerPageOptions'>
}

export const DispatchDataTableModal = (props: DispatchDataTableModalProps) => {
  const {t} = useTranslation(["common"]);

  const {
    title = t<string>("common.entity.dispatch.plural", "Container Clusters"),
    dataTable,
    ...rest
  } = props;

  return (
    <Modal {...rest} title={title}>
      <Box>
        <DispatchDataTable
          {...dataTable}
          recordsPerPageOptions={[5]}/>
      </Box>
    </Modal>
  );
};
